/* ============================================================================
 * svnty-pdp.js — THE PRODUCT PAGE behaviour (SVNTY, 2026-09-19).
 * Classic deferred script, no deps, ASCII only, no console.
 *
 * What it owns:
 *   1. THE BUY BUTTON's words. Trade relabels the button from window.variantStrings
 *      on every variant change; SVNTY's law is that a disabled button says the NEXT
 *      ACTION. So we write the label last: on the bubbled `change` (after
 *      variant-selects has resolved the variant) and again on PUB_SUB_EVENTS
 *      .variantChange, which Trade publishes at the very end of renderProductInfo.
 *   2. The chosen size printed beside the option name (SIZE - M). Trade only does
 *      this for swatch options (data-selected-swatch-value); pills had nothing.
 *   3. The size-guide link: opens the SIZE GUIDE tab and scrolls to it. Also
 *      answers the `svnty:open-size-guide` event the sticky bar dispatches.
 *   4. NOTIFY-ME: shown only while the chosen variant is unavailable, with the
 *      colour and size named in its line, submitted to the App Proxy by fetch.
 *      It is NOT a Shopify native contact form on purpose: Shopify's storefront
 *      hCaptcha auto-hooks such a form's submit and fires a real form.submit()
 *      ~300ms later, ignoring preventDefault (the sibling/a sibling store lesson). Nothing here is
 *      ever re-mounted or re-injected.
 *
 * The Ref. line needs no JS: it rides Trade's own Sku-<section> swap.
 * ========================================================================== */
(function () {
  'use strict';

  /* WORDS (H4): every sentence this file writes into the DOM comes from `window.svntyStrings`,
     rendered by Liquid from `locales/*.json` in snippets/svnty-head.liquid, or from the
     `data-*` attribute the server already printed. No English literal lives in this file. */
  var STR = window.svntyStrings || {};

  function text(el, value) {
    if (el) el.textContent = value;
  }

  function optionKind(name) {
    var n = String(name || '').toLowerCase();
    if (n.indexOf('size') !== -1) return 'size';
    if (n.indexOf('colour') !== -1 || n.indexOf('color') !== -1) return 'colour';
    return 'other';
  }

  function Pdp(root) {
    this.root = root;
    this.selects = root.querySelector('variant-selects');
    this.form = root.querySelector('product-form form');
    this.addBtn = root.querySelector('.product-form__submit');
    this.addSpan = this.addBtn ? this.addBtn.querySelector('span') : null;
    this.notify = root.querySelector('[data-svnty-notify]');
    this.variants = this.readVariants();
    this.optionNames = this.readOptionNames();
    this.sync = this.sync.bind(this);
  }

  Pdp.prototype.readVariants = function () {
    if (!this.selects) return [];
    var tag = this.selects.querySelector('script[type="application/json"]');
    if (!tag) return [];
    try {
      var parsed = JSON.parse(tag.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  /* the option names, in position order, as the page prints them */
  Pdp.prototype.readOptionNames = function () {
    if (!this.selects) return [];
    return Array.prototype.map.call(this.selects.querySelectorAll('.product-form__input'), function (wrap) {
      return wrap.dataset.svntyOption || '';
    });
  };

  /* the chosen value of every option, in position order (Trade's own reading) */
  Pdp.prototype.chosen = function () {
    if (!this.selects) return [];
    return Array.prototype.map.call(this.selects.querySelectorAll('select, fieldset'), function (el) {
      if (el.tagName === 'SELECT') return el.value;
      var hit = Array.prototype.find.call(el.querySelectorAll('input'), function (radio) {
        return radio.checked;
      });
      return hit ? hit.value : '';
    });
  };

  /* THE STRIPE NAME (D8-8, R239): the chosen value of every option as the page prints it: a colour input carries its printed
     name in data-svnty-name ("Blue stripe", snippets/product-variant-options.liquid); any other value prints as it is */
  Pdp.prototype.chosenNames = function () {
    if (!this.selects) return [];
    return Array.prototype.map.call(this.selects.querySelectorAll('select, fieldset'), function (el) {
      if (el.tagName === 'SELECT') {
        var o = el.options[el.selectedIndex];
        return (o && o.getAttribute('data-svnty-name')) || el.value;
      }
      var hit = Array.prototype.find.call(el.querySelectorAll('input'), function (radio) {
        return radio.checked;
      });
      return hit ? hit.getAttribute('data-svnty-name') || hit.value : '';
    });
  };

  Pdp.prototype.currentVariant = function () {
    if (!this.variants.length) return null;
    if (!this.selects) return this.variants[0] || null;
    var picked = this.chosen();
    return (
      this.variants.find(function (variant) {
        return (variant.options || []).every(function (value, index) {
          return picked[index] === value;
        });
      }) || null
    );
  };

  /* the name of the option whose current value leaves no variant: what to choose next */
  Pdp.prototype.missingOptionName = function () {
    var names = this.optionNames;
    /* P17-26: sentence case inside the sentence ("Select a size", as the sticky bar says it; the caps are the button's CSS) */
    for (var i = 0; i < names.length; i += 1) {
      if (optionKind(names[i]) === 'size') return names[i].toLowerCase();
    }
    if (names.length) return names[names.length - 1].toLowerCase();
    return (STR.size_label || '').toLowerCase();
  };

  /* ---------------------------------------------------------- the buy button */

  Pdp.prototype.syncButton = function (variant) {
    if (!this.addBtn || !this.addSpan) return;
    var labels = this.addBtn.dataset;
    /* THE DOOR (D5-3, C-04): no size is chosen for the shopper; until a size is picked the button is disabled and says the
       next action, and BUY IT NOW waits with it (inert) */
    var pay = this.root.querySelector('.shopify-payment-button');
    if (pay) {
      if (this.noSize) pay.setAttribute('inert', ''); else pay.removeAttribute('inert');
      /* P21-1: a sold-out size (or no such piece) takes the wallet button with it; until a size is chosen it rests in full ink */
      if (!this.noSize && (!variant || !variant.available)) pay.setAttribute('data-svnty-pay-out', ''); else pay.removeAttribute('data-svnty-pay-out');
      this.keepPay(pay);
    }
    if (this.noSize || !variant) {
      this.holdQuantity(false);
      this.addBtn.setAttribute('disabled', 'disabled');
      text(
        this.addSpan,
        (labels.svntySelectLabel || STR.select_option || '').replace('[option]', this.missingOptionName())
      );
      return;
    }
    if (!variant.available) {
      this.addBtn.setAttribute('disabled', 'disabled');
      text(this.addSpan, labels.svntySoldoutLabel || STR.sold_out || '');
      this.holdQuantity(true);
      return;
    }
    this.holdQuantity(false);
    this.addBtn.removeAttribute('disabled');
    text(this.addSpan, labels.svntyAddLabel || STR.add_to_bag || '');
  };

  /* P21-1 THE WALLET BUTTON RESTS IN INK (the owner, 2026-10-01: "fully black"). The store's script dims its button whenever
     ADD is disabled, so before a size was chosen the page showed a gray wallet button. While no size is chosen the page lifts
     that flag (each time the script sets it) and the slot stays inert, so the size the theme pre-checks is never bought; a
     tap on the resting button brings the sizes into view. With a size chosen the button is the store's own again. */
  Pdp.prototype.keepPay = function (pay) {
    var self = this;
    function lift() {
      if (!self.noSize) return;
      var host = pay.querySelector('shopify-accelerated-checkout');
      if (host && host.hasAttribute('disabled')) host.removeAttribute('disabled');
    }
    lift();
    if (pay.__svntyPayWatch || !window.MutationObserver) return;
    pay.__svntyPayWatch = new MutationObserver(lift);
    pay.__svntyPayWatch.observe(pay, { attributes: true, attributeFilter: ['disabled'], subtree: true, childList: true });
    /* an inert slot takes no tap: the click lands on the row behind it, so the row answers for it */
    this.root.addEventListener('click', function (event) {
      if (!self.noSize || !pay.isConnected || !event.clientX) return;
      var b = pay.getBoundingClientRect();
      if (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom) return;
      var row = self.root.querySelector('.product-form__input--pill[data-svnty-option*="size" i]');
      if (!row) return;
      var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' });
    });
  };

  /* P17-26 (eye pass R2 P3-3): a sold-out size says it once, on the button (the price's badge and the stock line wait,
     svnty-pdp.css), and its quantity stepper rests: inert (no tap, no Tab stop) and dimmed; Trade's quantity re-render keeps
     the element, so the state holds through a variant change */
  Pdp.prototype.holdQuantity = function (hold) {
    var q = this.root.querySelector('quantity-input');
    if (!q) return;
    if (hold) { q.setAttribute('inert', ''); q.classList.add('svnty-qty--rest'); }
    else { q.removeAttribute('inert'); q.classList.remove('svnty-qty--rest'); }
  };

  /* ------------------------------------------------- the chosen value, printed */

  Pdp.prototype.syncOptionValues = function () {
    if (!this.selects) return;
    var picked = this.chosenNames();
    var wraps = this.selects.querySelectorAll('.product-form__input');
    var self = this;
    Array.prototype.forEach.call(wraps, function (wrap, index) {
      var out = wrap.querySelector('[data-svnty-selected-value]');
      if (out) text(out, self.noSize && optionKind(self.optionNames[index]) === 'size' ? '' : picked[index] || '');
    });
  };

  /* ------------------------------------------------------------- notify-me: THE NOTIFY CARD v3 on Shopify's own customer form (P27, TH-2;
     02_architecture/flows/NOTIFY_ME_NATIVE_Part27.md). The card's form posts natively (no fetch): Shopify's bot check may intercept the
     submit and post it itself after its challenge (the footer's D5-2 pattern, assets/svnty-footer.js). Every variant change rewrites the
     hidden tags (notify-me,bis-<id>,bisp-<handle>); the stamp (assets/svnty-notify.js) names the variant the post was for, and the page the
     post returns to shows "done" only when Shopify says posted AND the stamp names this variant. The memory is written on that return, never
     before the post: an abandoned challenge never shows "already". */

  function notifyLane() { return window.SVNTY && window.SVNTY.notify ? window.SVNTY.notify : null; }

  Pdp.prototype.notifyPick = function (variant) {
    var parts = [], names = this.optionNames, picked = this.chosenNames();
    for (var i = 0; i < names.length; i += 1) {
      var kind = optionKind(names[i]);
      if ((kind === 'colour' || kind === 'size') && picked[i]) parts.push(picked[i]);
    }
    if (!parts.length && variant && variant.title) parts.push(variant.title);
    return parts.join(' · ');
  };

  Pdp.prototype.syncNotify = function (variant) {
    var block = this.notify;
    if (!block) return;
    var show = !this.noSize && !!variant && !variant.available;
    block.hidden = !show;
    if (!show) return;
    var pick = this.notifyPick(variant);
    var line = block.querySelector('[data-svnty-notify-line]');
    if (line) text(line, (line.dataset.svntyTemplate || STR.notify_line || '').replace('[pick]', pick));
    Array.prototype.forEach.call(block.querySelectorAll('[data-svnty-notify-state][data-svnty-template]'), function (node) {
      node.textContent = (node.dataset.svntyTemplate || '').replace('[pick]', pick);
    });
    var lane = notifyLane();
    var tags = block.querySelector('[data-svnty-notify-tags]');
    var vid = String(variant.id);
    var changed = !!tags && block.__svntyNotifyVid !== undefined && block.__svntyNotifyVid !== vid;
    if (tags && lane) tags.value = lane.tags(vid, block.getAttribute('data-svnty-notify-handle'));
    if (!block.__svntyNotifyInit) {
      block.__svntyNotifyInit = true;
      block.__svntyNotifyVid = vid;
      this.notifyReturn(block, vid);
      return;
    }
    block.__svntyNotifyVid = vid;
    if (changed) this.notifyState(block, lane && lane.remembered(vid) ? 'already' : 'idle', true);
  };

  /* the page a native post returned to: Shopify's word (posted / errors) read together with the stamp */
  Pdp.prototype.notifyReturn = function (block, vid) {
    var lane = notifyLane();
    var fields = block.querySelector('[data-svnty-notify-form]');
    var stamp = lane ? lane.takeStamp() : null;
    var mine = !!stamp && stamp.variant === vid;
    var posted = !!fields && fields.getAttribute('data-svnty-notify-posted') === 'true';
    var errors = !!fields && fields.getAttribute('data-svnty-notify-errors') === 'true';
    var email = block.querySelector('[name="contact[email]"]');
    if (mine && posted && !errors) {
      lane.clearStamp(); lane.remember(vid);
      this.notifyState(block, 'done', true);
      this.notifyReveal(block);
      return;
    }
    if (mine && errors) {
      lane.clearStamp();
      if (email) email.setAttribute('aria-invalid', 'true');
      this.notifyState(block, 'email', true);
      this.notifyReveal(block);
      return;
    }
    if (email && !mine) email.value = '';   /* another form's post: never this card's answer, never its address */
    this.notifyState(block, lane && lane.remembered(vid) ? 'already' : 'idle', true);
  };

  Pdp.prototype.notifyReveal = function (block) {
    var states = block.querySelector('[data-svnty-notify-states]');
    try { block.scrollIntoView({ block: 'center' }); } catch (e) { /* old engine */ }
    if (states) { try { states.focus({ preventScroll: true }); } catch (e) { /* no focus */ } }
  };

  var NOTIFY_FINAL = { done: 1, already: 1, cap: 1, error: 1 };
  Pdp.prototype.notifyState = function (block, state, quiet) {
    var form = block.querySelector('[data-svnty-notify-form]');
    if (form) form.hidden = !(state === 'idle' || state === 'email' || state === 'consent' || state === 'submitting');
    Array.prototype.forEach.call(block.querySelectorAll('[data-svnty-notify-state]'), function (node) {
      node.hidden = node.dataset.svntyNotifyState !== state;
    });
    var again = block.querySelector('[data-svnty-notify-again]');
    if (again) again.hidden = !NOTIFY_FINAL[state];
    var submit = block.querySelector('[data-svnty-notify-submit]');
    if (submit) { if (state === 'submitting') submit.setAttribute('aria-busy', 'true'); else submit.removeAttribute('aria-busy'); }
    var box = block.querySelector('.svnty-notify__consent');
    if (box) box.classList.toggle('is-invalid', state === 'consent');
    var states = block.querySelector('[data-svnty-notify-states]');
    if (!quiet && states && NOTIFY_FINAL[state]) { try { states.focus({ preventScroll: true }); } catch (e) { /* no focus */ } }
  };

  Pdp.prototype.wireNotify = function () {
    var self = this;
    var block = this.notify;
    if (!block) return;
    var form = block.querySelector('form.svnty-notify__native');
    if (!form) return;
    var email = form.querySelector('[name="contact[email]"]');
    var consent = form.querySelector('[data-svnty-notify-consent]');
    var again = block.querySelector('[data-svnty-notify-again]');
    if (again) again.addEventListener('click', function () {
      self.notifyState(block, 'idle', true);
      if (email) { try { email.focus({ preventScroll: true }); } catch (e) { /* no focus */ } }
    });
    /* the browser's own required checks name our states (no native bubble), before any post */
    form.addEventListener('invalid', function (event) {
      event.preventDefault();
      if (event.target === consent && !(email && !email.validity.valid)) self.notifyState(block, 'consent', true);
      else if (event.target === email) { email.setAttribute('aria-invalid', 'true'); self.notifyState(block, 'email', true); }
    }, true);
    if (consent) consent.addEventListener('change', function () { if (consent.checked) { var b = block.querySelector('.svnty-notify__consent'); if (b) b.classList.remove('is-invalid'); } });
    if (email) email.addEventListener('input', function () { email.removeAttribute('aria-invalid'); });
    var busyTimer = 0;
    window.addEventListener('pageshow', function () { window.clearTimeout(busyTimer); var s = block.querySelector('[data-svnty-notify-submit]'); if (s) s.removeAttribute('aria-busy'); });
    form.addEventListener('submit', function (event) {
      var lane = notifyLane();
      var value = email ? String(email.value || '').trim() : '';
      var vid = String(block.__svntyNotifyVid || '');
      if (!lane || !lane.on) { event.preventDefault(); self.notifyState(block, 'error'); return; }
      if (!lane.validEmail(value)) {
        event.preventDefault();
        if (email) { email.setAttribute('aria-invalid', 'true'); try { email.focus({ preventScroll: true }); } catch (e) { /* no focus */ } }
        self.notifyState(block, 'email', true);
        return;
      }
      if (consent && !consent.checked) { event.preventDefault(); self.notifyState(block, 'consent', true); return; }
      if (lane.cap(vid)) { event.preventDefault(); self.notifyState(block, 'cap'); return; }
      if (email) email.removeAttribute('aria-invalid');
      var tags = form.querySelector('[data-svnty-notify-tags]');
      if (tags) tags.value = lane.tags(vid, block.getAttribute('data-svnty-notify-handle'));
      lane.stamp(vid, 'pdp');
      /* the native post goes out (or Shopify's check takes it and posts it itself): busy only once it really leaves, never past 10 s */
      window.setTimeout(function () {
        if (event.defaultPrevented) return;
        var s = block.querySelector('[data-svnty-notify-submit]'); if (s) s.setAttribute('aria-busy', 'true');
        window.clearTimeout(busyTimer);
        busyTimer = window.setTimeout(function () { if (s) s.removeAttribute('aria-busy'); }, 10000);
      }, 0);
    });
  };

  /* ------------------------------------------------------------ size guide */

  function openSizeGuide(event) {
    /* C-03 (D5-3): a link inside a door opens the DOOR product's sheet (svnty-size-sheet.js answers it in its capture
       handler); only the page's own links reach this */
    if (event && event.target && event.target.closest && event.target.closest('quick-add-modal')) return;
    /* D2-23: THE SIZE SHEET (svnty-size-sheet.js) owns the tap when it is on the page; the tab is the fallback. */
    if (window.SVNTY && window.SVNTY.sizeSheet) { window.SVNTY.sizeSheet.open(); return; }
    var details = document.querySelector('details[data-svnty-tab="size_guide"]');
    if (!details) return;
    details.open = true;
    try {
      details.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      details.scrollIntoView();
    }
  }

  Pdp.prototype.wireSizeGuide = function () {
    var links = this.root.querySelectorAll('[data-svnty-size-guide]');
    Array.prototype.forEach.call(links, function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        openSizeGuide(event);
      });
    });
  };

  /* ------------------------------------------------------------------ wiring */

  Pdp.prototype.sync = function () {
    var variant = this.currentVariant();
    this.syncOptionValues();
    this.syncButton(variant);
    this.syncNotify(variant);
  };

  Pdp.prototype.start = function () {
    var self = this;
    this.wireSizeGuide();
    this.wireNotify();
    if (this.selects) {
      /* the change bubbles past variant-selects' own handler, so the variant is resolved by now */
      this.selects.addEventListener('change', function () {
        self.sync();
      });
    }
    this.sync();
  };

  /* THE DOOR (D5-3, C-02): the quick-add door carries the product section without its <section> wrapper, so the boot below
     never saw it and the door kept Trade's words ("Add to cart") and a stale Color label. mount(root) gives a door root the
     same behaviour, scoped to that root; the page's own sections are never touched by it. {noSize: true} starts the door
     with no size chosen (C-04). A root is mounted once; a closed door's instance drops out of the list. */
  var pages = [];
  /* P20: the size she chose, kept for this tab (see mount) */
  function sizeKey() { return 'svnty:size-chosen:' + location.pathname; }
  function sizeKept(pdp) {
    try { var v = pdp.currentVariant(); return !!v && sessionStorage.getItem(sizeKey()) === String(v.id); } catch (e) { return false; }
  }
  function keepSize(pdp) {
    if (pdp.noSize || pdp.root.closest('quick-add-modal')) return;
    try { var v = pdp.currentVariant(); if (v) sessionStorage.setItem(sizeKey(), String(v.id)); } catch (e) {}
  }
  function alive(pdp) { return pdp.root.isConnected && (!pdp.selects || pdp.root.contains(pdp.selects)); }
  function mount(root, opts) {
    if (!root) return null;
    if (root.__svntyPdp && alive(root.__svntyPdp)) return root.__svntyPdp;   /* a door's root is reused: its old content is gone */
    var pdp = new Pdp(root);
    pdp.noSize = !!(opts && opts.noSize) && pdp.optionNames.some(function (n) { return optionKind(n) === 'size'; });
    /* P20: a size she chose stays chosen across a refresh and the way back. The tap is remembered for this tab (the piece's
       address and the variant it made), and a page that opens on that very variant keeps it; a card's link opens its color's
       first size, another variant, so the address still chooses nothing for her. Never in a door. */
    if (pdp.noSize && !root.closest('quick-add-modal') && sizeKept(pdp)) {
      pdp.noSize = false;
      setTimeout(function () { root.dispatchEvent(new CustomEvent('svnty:size:chosen', { bubbles: true })); }, 0);
    }
    root.__svntyPdp = pdp;
    if (pdp.noSize && pdp.selects) {
      /* a tap on a size (even the one Trade pre-checked, which fires no change) is the choice */
      pdp.selects.addEventListener('click', function (event) {
        var input = event.target && event.target.closest && event.target.closest('input[type="radio"], label');
        if (!input) return;
        var wrap = input.closest('.product-form__input');
        var idx = Array.prototype.indexOf.call(pdp.selects.querySelectorAll('.product-form__input'), wrap);
        if (idx < 0 || optionKind(pdp.optionNames[idx]) !== 'size') return;
        pdp.noSize = false;
        /* P17-26: the state leaves in the same task as the sync, so no frame shows the stock line's stale words; the event
           tells the phone's sticky bar (a tap on the pre-checked size changes no radio, so no change event reaches it) */
        setTimeout(function () {
          pdp.sync();
          keepSize(pdp);
          if (!root.classList.contains('svnty-door--nosize')) return;
          root.classList.remove('svnty-door--nosize');
          root.dispatchEvent(new CustomEvent('svnty:size:chosen', { bubbles: true }));
        }, 0);
      });
      root.classList.add('svnty-door--nosize');
    } else root.classList.remove('svnty-door--nosize');
    pdp.start();
    pages.push(pdp);
    return pdp;
  }
  window.SVNTY = window.SVNTY || {};
  window.SVNTY.pdpMount = mount;

  function boot() {
    var roots = document.querySelectorAll('section[id^="MainProduct-"]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.closest('quick-add-modal')) return;
      /* P17-26 (eye pass R2 P1-1): the page follows THE DOOR's rule (C-04): no size is chosen for the shopper. Liquid prints
         the no-size state on the section of a piece with two sizes or more (sections/main-product.liquid); every card link
         carries ?variant= for its color, so the address never counts as a size choice */
      mount(root, { noSize: root.classList.contains('svnty-door--nosize') });
    });
    /* a door already open when this file first runs (it arrives with the first door on a page that had no product) */
    Array.prototype.forEach.call(document.querySelectorAll('quick-add-modal[open] [id^="QuickAddInfo-"]'), function (root) {
      if (root.querySelector('variant-selects, product-form')) mount(root, { noSize: true });
    });

    document.addEventListener('svnty:open-size-guide', openSizeGuide);

    /* Trade publishes this at the END of renderProductInfo, after its own
       toggleAddButton — so SVNTY's words are the last written. */
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, function () {
          pages = pages.filter(alive);
          pages.forEach(function (pdp) {
            pdp.sync();
            keepSize(pdp);
          });
        });
      }
    } catch (e) {
      /* global.js absent: the bubbled change alone still keeps the button honest */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
